"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processSkillsMutations = processSkillsMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _idNodeFactory = require("../shared/idNodeFactory");
var _texts = require("../../texts");
const ESCO_SKILL_BASE_URI = 'http://data.europa.eu/esco/skill/';
const SKILL_PREFIX_BASE_URI = 'skill:';
function ensureSkillPrefix(store) {
  const anyStore = store;
  if (typeof anyStore.setPrefixForURI === 'function') {
    anyStore.setPrefixForURI('skill', SKILL_PREFIX_BASE_URI);
    return;
  }
  if (!anyStore.namespaces) {
    anyStore.namespaces = {};
  }
  anyStore.namespaces.skill = SKILL_PREFIX_BASE_URI;
}
function normalizeSkillPublicIdUri(publicId) {
  const normalized = publicId.trim();
  if (!normalized) return normalized;
  if (normalized.startsWith('skill:')) {
    return normalized;
  }
  if (normalized.startsWith(ESCO_SKILL_BASE_URI)) {
    const suffix = normalized.slice(ESCO_SKILL_BASE_URI.length);
    return suffix ? `skill:${suffix}` : normalized;
  }
  return normalized;
}
function normalizeNodeId(value) {
  return value.startsWith('_:') ? value.slice(2) : value;
}
function collectSkillLinkStatementsByEntryValue(store, subject, doc, entryNode) {
  const normalizedEntryNode = normalizeNodeId(entryNode);
  return store.statementsMatching(subject, _solidUi.ns.schema('skills'), null, doc).filter(statement => {
    const objectValue = normalizeNodeId(statement.object.value);
    return objectValue === normalizedEntryNode || statement.object.value === entryNode;
  });
}
function buildSkillsStatements(subject, doc, node, skill) {
  if (!skill.name) return [];
  if (!skill.publicId) {
    throw new Error(`Missing skill publicId for skill: ${skill.name}`);
  }
  const publicIdNode = (0, _rdflib.sym)(normalizeSkillPublicIdUri(skill.publicId));
  return [(0, _rdflib.st)(subject, _solidUi.ns.schema('skills'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.solid('publicId'), publicIdNode, doc), (0, _rdflib.st)(publicIdNode, _solidUi.ns.schema('name'), (0, _rdflib.literal)(skill.name), doc)];
}
async function mutateSkillsEntries(store, subject, skillOps) {
  ensureSkillPrefix(store);
  const doc = subject.doc();
  const existingSkillNodes = store.each(subject, _solidUi.ns.schema('skills'));
  const deletions = [];
  const insertions = [];
  const collectLinkedPublicIdStatements = skillNode => {
    const linkedPublicIdStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, skillNode, _solidUi.ns.solid('publicId'), doc);
    deletions.push(...linkedPublicIdStatements.linkedStatements);
  };
  skillOps.remove.forEach(skill => {
    if (!skill.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingSkillNodes, skill.entryNode);
    deletions.push(...collectSkillLinkStatementsByEntryValue(store, subject, doc, skill.entryNode));
    if (existingNode) {
      if (existingNode.termType !== 'Literal') {
        deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
        if (existingNode.termType === 'NamedNode') {
          collectLinkedPublicIdStatements(existingNode);
        }
      }
    }
  });
  skillOps.update.forEach(skill => {
    if (!skill.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingSkillNodes, skill.entryNode);
    if (!existingNode) {
      insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
      return;
    }
    deletions.push(...collectSkillLinkStatementsByEntryValue(store, subject, doc, skill.entryNode));
    if (existingNode.termType !== 'Literal') {
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
      if (existingNode.termType === 'NamedNode') {
        collectLinkedPublicIdStatements(existingNode);
      }
    }
    if (existingNode.termType === 'NamedNode') {
      insertions.push(...buildSkillsStatements(subject, doc, existingNode, skill));
      return;
    }
    insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
  });
  skillOps.create.forEach(skill => {
    insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
  });
  await (0, _rdfMutationHelpers.applyUpdaterPatch)(store, deletions, insertions);
}
async function processSkillsMutations(store, subject, mutationPlan) {
  try {
    await mutateSkillsEntries(store, subject, mutationPlan);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`${_texts.mutationSaveSkillsFailedPrefixText} ${message}`);
  }
}